import { processAudioRecording } from "../../../../services/api-documentation";
import { logUIError } from "../../../../services/api-misc";
import LocalStorageService from "../../../../services/localstorage";
import { mixpanel } from "../../../../utils/mixpanel";
import { TSession } from "../../../../utils/types";

export async function retryFailedVoiceNotes({
  session,
  onSaveRecording,
}: {
  session: TSession;
  onSaveRecording: (result: {
    transcription: string;
    summary: string;
    audioUrl: string;
    audioId: number;
    noteType: string;
  }) => void;
}) {
  const recordings = await LocalStorageService.getPendingRecordings();
  const filteredRecordings = recordings.filter(
    (recording) => recording.metadata.clinicId === session.clinicId,
  );

  for (const recording of filteredRecordings) {
    try {
      const patientIdentifier = recording.metadata.patientClinicOSUUID;

      if (!patientIdentifier) {
        console.warn(
          "🧹 Deleting stale pending recording without patient UUID:",
          recording.id,
        );
        await LocalStorageService.deleteRecording(recording.id);
        continue;
      }

      await LocalStorageService.updateRecordingStatus(
        recording.id,
        "uploading",
        true,
      );

      const result = await processAudioRecording(
        session,
        recording.audioBlob,
        patientIdentifier,
        recording.metadata.noteType,
        undefined,
        recording.metadata.noteDateOverride,
      );

      onSaveRecording({
        transcription: result.transcription,
        summary: result.summary,
        audioUrl: result.audioUrl,
        audioId: result.audioId,
        noteType: recording.metadata.noteType,
      });

      await LocalStorageService.deleteRecording(recording.id);
      console.log("✅ Retried and uploaded:", recording.id);

      const successProps: Record<string, unknown> = {
        "User Email": session.userEmail,
        "Clinic Name": session.clinicName,
        "Patient ID": patientIdentifier,
        "Note Type": recording.metadata.noteType,
      };
      if (recording.metadata.patientClinicOSUUID) {
        successProps["Patient ClinicOS UUID"] =
          recording.metadata.patientClinicOSUUID;
        successProps["Legacy Patient Record ID"] = recording.metadata.patientId;
      }

      mixpanel.track("Voice Note Retry Upload Success", successProps);
    } catch (err) {
      console.error("❌ Retry failed:", err);
      await LocalStorageService.updateRecordingStatus(
        recording.id,
        "failed",
        true,
      );

      const failureProps: Record<string, unknown> = {
        "User Email": session.userEmail,
        "Clinic Name": session.clinicName,
        "Patient ID": recording.metadata.patientClinicOSUUID,
        "Note Type": recording.metadata.noteType,
        Error: err instanceof Error ? err.message : "Unknown error",
      };
      if (recording.metadata.patientClinicOSUUID) {
        failureProps["Patient ClinicOS UUID"] =
          recording.metadata.patientClinicOSUUID;
        failureProps["Legacy Patient Record ID"] = recording.metadata.patientId;
      }

      mixpanel.track("Voice Note Retry Upload Failed", failureProps);

      try {
        await logUIError({
          message: "Voice note retry upload failed",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          user_email: session.userEmail || undefined,
          clinic_name: session.clinicName || undefined,
          clinic_id: session.clinicId || undefined,
          context: {
            component: "retryFailedVoiceNotes",
            recordingId: recording.id,
            patientId: recording.metadata.patientId,
            patientClinicOSUUID: recording.metadata.patientClinicOSUUID,
            noteType: recording.metadata.noteType,
            error: err instanceof Error ? err.message : String(err),
          },
        });
      } catch (uiLogError) {
        console.warn("logUIError failed in retryFailedVoiceNotes", uiLogError);
      }
    }
  }
}
